/**
 * One item in the Settings → UI → Toolbar panel, wherever it currently lives.
 *
 * The checkbox means "on the toolbar row". Unchecking an item that has a More
 * group sends it back there; a default row item (Terminal, AI ▾ …) has no
 * group to fall back to, so unchecking it hides it — the row says so rather
 * than letting the item disappear somewhere the user has to go looking for.
 */
import type { ReactNode } from 'react';
import { EyeOff, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MORE_GROUP_LABELS, type ToolbarItemId } from '../../../model/toolbarItemTypes';
import { toolbarCatalogEntry } from '../../../model/toolbarItemCatalog';

/** Why a full row blocks a promotion — the alternative is a mute no-op. */
const FULL_ROW_HINT =
  'The toolbar row is full. Hide something, or move something off the row, to make space.';

export type ToolbarCustomizationRowProps = {
  id: ToolbarItemId;
  placement: 'row' | 'more' | 'hidden';
  /** Blocks further promotion once the row is full. */
  canPromote: boolean;
  /** This item would not render for the connections currently open. */
  unavailable?: boolean;
  onToggleRow: (id: ToolbarItemId) => void;
  onHide: (id: ToolbarItemId) => void;
  /** Only reachable from the Hidden list. */
  onRestore?: (id: ToolbarItemId) => void;
  dragHandle?: ReactNode;
};

export function ToolbarCustomizationRow({
  id,
  placement,
  canPromote,
  unavailable = false,
  onToggleRow,
  onHide,
  onRestore,
  dragHandle,
}: ToolbarCustomizationRowProps) {
  const entry = toolbarCatalogEntry(id);
  const Icon = entry.icon;
  const onRow = placement === 'row';
  const anchored = !entry.hideable;
  const fallsBackToHidden = onRow && entry.group === undefined;
  // A restore only lands back ON the row for an item with no More group;
  // everything else returns to its group and can never overflow the cap.
  const restoreBlocked = entry.group === undefined && !canPromote;

  return (
    <div
      data-testid={`toolbar-customize-row-${id}`}
      data-item-id={id}
      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50"
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground/60">
        {dragHandle}
      </span>
      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
        {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
      </span>
      <span className="truncate">{entry.label}</span>
      {unavailable && (
        <span
          className="shrink-0 rounded-sm border border-border px-1.5 py-px text-[10px] text-muted-foreground"
          title="This one only appears while a connection that supports it is open — your choice is still saved for then."
          data-testid={`toolbar-customize-unavailable-${id}`}
        >
          Not available now
        </span>
      )}
      <span className="flex-1" />

      {anchored ? (
        <span className="text-xs text-muted-foreground">
          {entry.anchor === 'first' ? 'Always first' : 'Always last'}
        </span>
      ) : placement === 'hidden' ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs"
          disabled={restoreBlocked}
          title={restoreBlocked ? FULL_ROW_HINT : undefined}
          onClick={() => onRestore?.(id)}
          data-testid={`toolbar-customize-restore-${id}`}
        >
          <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
          Restore
        </Button>
      ) : (
        <>
          {!onRow && entry.group && (
            <span className="text-xs text-muted-foreground">{MORE_GROUP_LABELS[entry.group]}</span>
          )}
          <Checkbox
            checked={onRow}
            disabled={!onRow && !canPromote}
            onCheckedChange={() => onToggleRow(id)}
            aria-label={`Show ${entry.label} on the toolbar`}
            title={
              !onRow && !canPromote
                ? FULL_ROW_HINT
                : fallsBackToHidden
                  ? `${entry.label} has no place in the More ▾ menu — unchecking it hides it instead.`
                  : undefined
            }
            className="h-3.5 w-3.5"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            onClick={() => onHide(id)}
            aria-label={`Hide ${entry.label} from the toolbar`}
            title={`Hide ${entry.label} from the toolbar entirely`}
            data-testid={`toolbar-customize-hide-${id}`}
          >
            <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </>
      )}
    </div>
  );
}
